import { Analytics } from '@cobliteam/analytics';
import { useUser } from '@cobliteam/hooks';
import { MATURITY_ITEMS, type MaturityAreaKey, type MaturityItemId } from '@cobliteam/hooks/src/useBasicValueMaturity/basicValueMaturityModel';
import useGeofenceRecommendations from '@cobliteam/hooks/src/useGeofenceRecommendations/useGeofenceRecommendations';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import { type BasicValueStep, DialogBasicValueOnboardingView } from '@cobliteam/ui-business/components/dialogs/DialogBasicValueOnboarding/DialogBasicValueOnboarding';
import { useHistory } from 'react-router-dom';
import { ITEM_ICON } from '../../pages/BasicValuePage/basicValueMaturityPresentation';
import { buildItemTourSteps } from '../../pages/BasicValuePage/basicValueTour';
import MaturityTrackCompact from '../../pages/BasicValuePage/components/MaturityTrackCompact';
import useBasicValueGuideStore from '../../store/useBasicValueGuideStore';
import useCursorFlowStore from '../../store/useCursorFlowStore';
import { isSetupGuideEntityId } from '../SetupGuideModals/entityModalConfig';
import SetupGuideEntityModal from '../SetupGuideModals/SetupGuideEntityModal';
import { getStepNavigationTarget } from './basicValueStepNavigation';
import InstallationStepBody from './steps/InstallationStepBody';
import useBasicValueOnboardingSource, { type BasicValuePendingFeature } from './useBasicValueOnboardingSource';

/** Setup stage (area) of each item, used to highlight the right node in the modal's track. */
const AREA_BY_ITEM_ID = MATURITY_ITEMS.reduce(
  (acc, item) => {
    acc[item.id] = item.area;
    return acc;
  },
  {} as Record<MaturityItemId, MaturityAreaKey>
);

/**
 * Items resolved by redirecting straight to their creation page (AE-2444) — no inline creation nor
 * tutorial modal: geofences (map drawing can't be a modal), alerts and checklists.
 */
const NAVIGATE_IDS = new Set<MaturityItemId>(['geofences', 'alerts', 'checklists']);

const BasicValueOnboardingConnected = () => {
  const history = useHistory();
  const { t } = useTranslation('basic-value-onboarding');

  const openItemId = useBasicValueGuideStore((state) => state.openItemId);
  const closeGuide = useBasicValueGuideStore((state) => state.close);
  const isManual = openItemId !== null;

  const { pendingFeatures, areas, fleetId, refetch, onClose, markDismissed, snooze } = useBasicValueOnboardingSource({ forceActive: isManual });
  const startCursorFlow = useCursorFlowStore((state) => state.start);

  // "Criar locais" redirects with the FIRST suggestion prefilled (AE-2444). The hook is gated by
  // the poisRecommendations flag and caches per fleet — with no data the redirect falls back to the
  // plain creation route.
  const { data: geofenceRecommendations } = useGeofenceRecommendations({ enabled: isManual });

  // Manual-only: opened on demand from the home maturity hero or the Basic Value page. There is no
  // auto-popup anymore — the home hero is the entry point.
  if (!isManual) return null;

  const handleClose = () => {
    Analytics.track('BasicValueModalClosed', { fleetId, source: 'page' });
    closeGuide();
    onClose();
  };

  // Entities with a dedicated creation modal (AE-2443) skip the legacy multi-step dialog entirely:
  // card → creation form → success, all on the shared DialogSetupGuideShell.
  if (isSetupGuideEntityId(openItemId)) {
    return <SetupGuideEntityModal itemId={openItemId} fleetId={fleetId} onClose={handleClose} onCreated={markDismissed} refetchMaturity={refetch} />;
  }

  // Same order as the Basic Value page (MATURITY_ITEMS order, preserved by pendingFeatures).
  // Entities with a dedicated creation modal are excluded — they never render as steps here.
  const steps: BasicValueStep[] = pendingFeatures
    .filter((pending) => !isSetupGuideEntityId(pending.id))
    .map((pending: BasicValuePendingFeature) => ({
      id: pending.id,
      icon: ITEM_ICON[pending.id],
      title: t(`items.${pending.id}.title`),
      subtitle: t(`items.${pending.id}.description`),
      benefit: t(`benefits.${pending.id}`),
    }));

  if (steps.length === 0) return null;

  const routeById = new Map(pendingFeatures.map((feature) => [feature.id, feature.route]));

  const handleStepChange = (featureId: MaturityItemId, stepIndex: number) => {
    Analytics.track('BasicValueModalStepViewed', { fleetId, feature: featureId, stepIndex, totalSteps: steps.length, source: isManual ? 'page' : 'auto' });
  };

  const handleSnooze = () => {
    Analytics.track('BasicValueModalDontShowAgainClicked', { fleetId });
    closeGuide();
    snooze();
  };

  // Items without an inline step navigate to their creation page (AE-2444); closing the modal + a
  // cursor flow guides the user to the action to take there.
  const renderNavigateAction = (step: BasicValueStep) => {
    const route = routeById.get(step.id);
    if (!route) return null;
    // Geofences: first suggestion prefilled via ?recommendationId= (consumed by usePrefilledGeofence
    // on the creation page, mirroring GeofencesPage's goToCreate).
    const recommendationId = step.id === 'geofences' ? geofenceRecommendations?.[0]?.id : undefined;
    const target = getStepNavigationTarget(step.id, route, recommendationId);
    return (
      <Button
        onClick={() => {
          Analytics.track('BasicValueModalGoToFeatureClicked', { fleetId, feature: step.id, route: target, recommendationId });
          handleClose();
          history.push(target);
          const tourSteps = buildItemTourSteps(step.id, t);
          if (tourSteps) startCursorFlow(tourSteps);
        }}
      >
        {t('actions.configure')}
      </Button>
    );
  };

  return (
    <DialogBasicValueOnboardingView
      key={openItemId ?? 'auto'}
      steps={steps}
      initialStepId={openItemId ?? undefined}
      onClose={handleClose}
      onStepChange={handleStepChange}
      onDismissSnooze={isManual ? undefined : handleSnooze}
      renderTrack={(step) => <MaturityTrackCompact areas={areas} activeAreaKey={AREA_BY_ITEM_ID[step.id]} />}
      renderStepBody={(step) => {
        switch (step.id) {
          case 'devices':
            return <InstallationStepBody fleetId={fleetId} onLaunch={handleClose} />;
          default:
            return null;
        }
      }}
      renderStepAction={(step) => {
        if (NAVIGATE_IDS.has(step.id)) return renderNavigateAction(step);
        switch (step.id) {
          case 'devices':
            // Resolved inline in the body (installation list); footer keeps only nav/close.
            return null;
          default:
            return renderNavigateAction(step);
        }
      }}
    />
  );
};

const BasicValueOnboarding = () => {
  const { session } = useUser();
  // The source hook reads getSession()/fleet; only mount once a session exists.
  if (!session) return null;
  return <BasicValueOnboardingConnected />;
};

export default BasicValueOnboarding;
